import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { Button } from '@rocco/components/button';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import { useState } from 'react';
import { useNavigate } from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';

/**
 * Represents the options for sorting in the search component.
 * @interface OptionResult
 * @property {Array<{header: string, to: string}>} items - List of sorting options with headers and navigation paths.
 * @property {{header: string, to: string}} value - The currently selected sorting option.
 * @property {string} defaultSortName - The default sorting option name.
 */

export interface OptionResult {
    items: { header: string; to: string }[];
    value: { header: string; to: string };
    defaultSortName: string;
}

interface SearchSortProps {
    optionResult: OptionResult;
}

/**
 * Props for the SearchSort component.
 * @interface SearchSortProps
 * @property {OptionResult} optionResult - The sorting options and current selection.
 */

/**
 * A component for displaying and selecting sorting options in a search interface.
 * @param {SearchSortProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */

const SearchSort = ({ optionResult }: SearchSortProps) => {
    const navigate = useNavigate();
    const { items, value, defaultSortName } = optionResult;
    const [isOpen, setIsOpen] = useState(false);
    const defaultOption =
        value?.header === defaultSortName ? defaultSortName : value?.header;
    const activatedIndex = items.findIndex(
        (item: { header: string; to: string }) => item.header === value.header
    );
    const activeOption = items.find(
        (item: { header: string; to: string }) => item.header === value.header
    );
    const tracker = useTracker();

    const handleSortChange = (value: string) => {
        // BC-1204 SortClick event
        tracker.log('SortClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: {
                    sortBy: value,
                },
            },
        });
        navigate(items.find((item) => item.header === value)?.to ?? '');
    };

    const sortOptions = items.map((item: { header: string; to: string }) => ({
        title: item.header,
        value: item.header,
        selected: item.header === value.header,
    }));

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button color="transparent" size="compact">
                    <span>
                        {activatedIndex > 0 ? activeOption?.header : 'Sort By'}
                    </span>
                    <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:[transform:rotateX(180deg)]" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    defaultValue={defaultOption}
                    onValueChange={handleSortChange}
                >
                    {sortOptions.map(
                        (
                            option: {
                                title: string;
                                value: string;
                                selected: boolean;
                            },
                            index: number
                        ) => (
                            <label
                                key={index}
                                className="flex items-center space-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50 cursor-pointer"
                            >
                                <RadioGroupItem value={option.value} />
                                <span className="text-base">
                                    {option.title}
                                </span>
                            </label>
                        )
                    )}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    );
};

export default SearchSort;
